import React, { useEffect, useState } from "react";
import { api, uploadFile } from "../../api/client";
import ConfirmModal from "../../components/ConfirmModal";
import ReservationsAdmin from "./ReservationsAdmin";

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const ETATS = ["neuf", "bon", "moyen", "abime"];
const STATUTS = ["disponible", "reserve", "emprunte", "indisponible"];

const API_BASE = process.env.REACT_APP_API_BASE || "/api";

export default function BibliothequeAdmin() {
  const [tab, setTab] = useState("livres"); // livres | reservations | horaires
  const [demandesCount, setDemandesCount] = useState(0);

  // Polling léger du nombre de demandes en attente (60s)
  useEffect(() => {
    const fetchCount = () => {
      api.get("/bibliotheque/admin/reservations/compteurs")
        .then((c) => setDemandesCount(c.en_attente || 0))
        .catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="biblio-admin">
      <div className="biblio-admin-tabs">
        <button
          className={`biblio-admin-tab ${tab === "livres" ? "active" : ""}`}
          onClick={() => setTab("livres")}>
          📚 Catalogue
        </button>
        <button
          className={`biblio-admin-tab ${tab === "reservations" ? "active" : ""}`}
          onClick={() => setTab("reservations")}>
          📋 Réservations
          {demandesCount > 0 && (
            <span className="resa-badge">{demandesCount}</span>
          )}
        </button>
        <button
          className={`biblio-admin-tab ${tab === "horaires" ? "active" : ""}`}
          onClick={() => setTab("horaires")}>
          📅 Horaires & fermetures
        </button>
      </div>
      {tab === "livres" && <OngletLivres />}
      {tab === "reservations" && <ReservationsAdmin />}
      {tab === "horaires" && <OngletHoraires />}
    </div>
  );
}

// ===================== ONGLET LIVRES =====================

function OngletLivres() {
  const [livres, setLivres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);  // livre en cours d'édition ou {} pour créer
  const [photosEditing, setPhotosEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/bibliotheque/livres")
      .then((r) => setLivres(r.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(reload, []);

  const filtered = livres.filter((l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return l.titre.toLowerCase().includes(q) ||
           (l.auteur || "").toLowerCase().includes(q) ||
           (l.isbn || "").includes(q);
  });

  const handleDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.del(`/bibliotheque/admin/livres/${confirmDel.id}`);
      setConfirmDel(null);
      reload();
    } catch (e) {
      alert("Erreur lors de la suppression : " + (e.message || e));
    }
  };

  return (
    <div>
      <div className="biblio-admin-toolbar">
        <input
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="biblio-search-input"
          style={{ flex: 1 }}
        />
        <button className="btn-primary" onClick={() => setEditing({})}>
          + Nouveau livre
        </button>
      </div>

      {loading && <p className="muted">Chargement…</p>}

      {!loading && filtered.length === 0 && (
        <p className="muted" style={{ textAlign: "center", padding: "2rem" }}>
          {livres.length === 0 ? "Aucun livre. Ajoutez-en avec « Nouveau livre »." :
                                 "Aucun résultat."}
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <table className="biblio-admin-table">
          <thead>
            <tr>
              <th>Titre</th>
              <th>Auteur</th>
              <th>Genre</th>
              <th>État</th>
              <th>Statut</th>
              <th>Photos</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id}>
                <td><strong>{l.titre}</strong></td>
                <td>{l.auteur || "-"}</td>
                <td>{l.genre || "-"}</td>
                <td>{l.etat}</td>
                <td>
                  <span className="biblio-statut-badge"
                        data-statut={l.statut}>
                    {l.statut}
                  </span>
                </td>
                <td>
                  <button className="link-btn"
                          onClick={() => setPhotosEditing(l)}>
                    {l.cover ? "📷 Gérer" : "+ Ajouter"}
                  </button>
                </td>
                <td>
                  <button className="link-btn" onClick={() => setEditing(l)}>Modifier</button>
                  <button className="link-btn danger" onClick={() => setConfirmDel(l)}>Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <EditLivreModal livre={editing} onClose={() => setEditing(null)} onSaved={reload} />
      )}
      {photosEditing && (
        <PhotosModal livre={photosEditing} onClose={() => { setPhotosEditing(null); reload(); }} />
      )}
      {confirmDel && (
        <ConfirmModal
          titre="Supprimer ce livre"
          message={`Supprimer définitivement "${confirmDel.titre}" ? Toutes ses photos seront aussi supprimées.`}
          confirmLabel="Supprimer"
          danger
          onConfirm={handleDelete}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  );
}

function EditLivreModal({ livre, onClose, onSaved }) {
  const isNew = !livre.id;
  const [form, setForm] = useState({
    titre: livre.titre || "",
    auteur: livre.auteur || "",
    isbn: livre.isbn || "",
    editeur: livre.editeur || "",
    annee: livre.annee || "",
    genre: livre.genre || "",
    resume: livre.resume || "",
    etat: livre.etat || "bon",
    emplacement: livre.emplacement || "",
    statut: livre.statut || "disponible",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSave = async () => {
    if (!form.titre.trim()) {
      setError("Le titre est obligatoire");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      payload.annee = payload.annee ? parseInt(payload.annee, 10) : null;
      if (isNew) {
        await api.post("/bibliotheque/admin/livres", payload);
      } else {
        await api.put(`/bibliotheque/admin/livres/${livre.id}`, payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isNew ? "Nouveau livre" : "Modifier le livre"}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}
          <div className="form-grid">
            <label className="full">
              Titre *
              <input type="text" value={form.titre} onChange={setF("titre")} required />
            </label>
            <label>
              Auteur
              <input type="text" value={form.auteur} onChange={setF("auteur")} />
            </label>
            <label>
              ISBN
              <input type="text" value={form.isbn} onChange={setF("isbn")} />
            </label>
            <label>
              Éditeur
              <input type="text" value={form.editeur} onChange={setF("editeur")} />
            </label>
            <label>
              Année
              <input type="number" min="1000" max="2100"
                     value={form.annee} onChange={setF("annee")} />
            </label>
            <label>
              Genre
              <input type="text" value={form.genre} onChange={setF("genre")}
                     placeholder="Roman, Policier, Jeunesse..." />
            </label>
            <label>
              Emplacement
              <input type="text" value={form.emplacement} onChange={setF("emplacement")}
                     placeholder="Rayon A, étagère 3..." />
            </label>
            <label>
              État
              <select value={form.etat} onChange={setF("etat")}>
                {ETATS.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </label>
            <label>
              Statut
              <select value={form.statut} onChange={setF("statut")}>
                {STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="full">
              Résumé
              <textarea rows="5" value={form.resume} onChange={setF("resume")} />
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotosModal({ livre, onClose }) {
  const [livreFull, setLivreFull] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const reload = () => {
    api.get(`/bibliotheque/livres/${livre.id}`).then(setLivreFull);
  };

  useEffect(reload, [livre.id]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await uploadFile(`/bibliotheque/admin/livres/${livre.id}/photos`, fd);
      reload();
    } catch (err) {
      setError(err.message || "Erreur lors de l'envoi");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await api.del(`/bibliotheque/admin/photos/${photoId}`);
      reload();
    } catch (e) {
      alert("Erreur : " + (e.message || e));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Photos – {livre.titre}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}
          <label className="btn-primary"
                 style={{ display: "inline-block", cursor: "pointer", marginBottom: "1rem" }}>
            {uploading ? "Envoi en cours..." : "+ Ajouter une photo"}
            <input type="file" accept="image/*" hidden
                   onChange={handleUpload} disabled={uploading} />
          </label>
          <p className="muted" style={{ fontSize: "0.85em" }}>
            Formats acceptés : JPG, PNG, WebP. Taille max : 5 Mo.
          </p>
          {livreFull && livreFull.photos && livreFull.photos.length > 0 ? (
            <div className="biblio-photos-grid">
              {livreFull.photos.map((p) => (
                <div key={p.id} className="biblio-photo-item">
                  <img src={`${API_BASE}${p.url}`} alt="" />
                  <button className="biblio-photo-delete"
                          onClick={() => handleDelete(p.id)}
                          title="Supprimer">×</button>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted" style={{ textAlign: "center", padding: "1rem" }}>
              Aucune photo pour ce livre.
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// ===================== ONGLET HORAIRES =====================

function OngletHoraires() {
  const [horaires, setHoraires] = useState([]);
  const [fermetures, setFermetures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [nouvelleFermeture, setNouvelleFermeture] = useState({ date_debut: "", date_fin: "", motif: "" });
  const [confirmDelFermeture, setConfirmDelFermeture] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/bibliotheque/horaires")
      .then((r) => {
        setHoraires(r.horaires || []);
        setFermetures(r.fermetures || []);
      })
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const updateHoraire = (jour, field, value) => {
    setHoraires(horaires.map((h) =>
      h.jour === jour ? { ...h, [field]: value } : h
    ));
  };

  const saveHoraires = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.put("/bibliotheque/admin/horaires", { horaires });
      setMsg("Horaires enregistrés ✓");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setMsg("Erreur : " + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const addFermeture = async () => {
    if (!nouvelleFermeture.date_debut || !nouvelleFermeture.date_fin) {
      alert("Renseignez les dates");
      return;
    }
    try {
      await api.post("/bibliotheque/admin/fermetures", nouvelleFermeture);
      setNouvelleFermeture({ date_debut: "", date_fin: "", motif: "" });
      reload();
    } catch (e) {
      alert("Erreur : " + (e.message || e));
    }
  };

  const removeFermeture = async () => {
    if (!confirmDelFermeture) return;
    await api.del(`/bibliotheque/admin/fermetures/${confirmDelFermeture.id}`);
    setConfirmDelFermeture(null);
    reload();
  };

  if (loading) return <p className="muted">Chargement…</p>;

  return (
    <div>
      <h3>Horaires hebdomadaires</h3>
      <p className="muted" style={{ fontSize: "0.9em" }}>
        Décochez « Fermé » pour activer les horaires d'un jour.
      </p>
      <table className="biblio-horaires-edit">
        <thead>
          <tr>
            <th>Jour</th>
            <th>Fermé</th>
            <th>Heure d'ouverture</th>
            <th>Heure de fermeture</th>
          </tr>
        </thead>
        <tbody>
          {horaires.map((h) => (
            <tr key={h.jour}>
              <td><strong>{JOURS[h.jour]}</strong></td>
              <td>
                <input type="checkbox" checked={!!h.ferme}
                       onChange={(e) => updateHoraire(h.jour, "ferme", e.target.checked ? 1 : 0)} />
              </td>
              <td>
                <input type="time" value={h.heure_ouverture}
                       disabled={!!h.ferme}
                       onChange={(e) => updateHoraire(h.jour, "heure_ouverture", e.target.value)} />
              </td>
              <td>
                <input type="time" value={h.heure_fermeture}
                       disabled={!!h.ferme}
                       onChange={(e) => updateHoraire(h.jour, "heure_fermeture", e.target.value)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginTop: "1rem" }}>
        <button className="btn-primary" onClick={saveHoraires} disabled={saving}>
          {saving ? "Enregistrement..." : "💾 Enregistrer les horaires"}
        </button>
        {msg && <span className="muted">{msg}</span>}
      </div>

      <h3 style={{ marginTop: "2rem" }}>Fermetures exceptionnelles</h3>
      <div className="biblio-fermeture-form">
        <label>
          Du :
          <input type="date" value={nouvelleFermeture.date_debut}
                 onChange={(e) => setNouvelleFermeture({...nouvelleFermeture, date_debut: e.target.value})} />
        </label>
        <label>
          Au :
          <input type="date" value={nouvelleFermeture.date_fin}
                 onChange={(e) => setNouvelleFermeture({...nouvelleFermeture, date_fin: e.target.value})} />
        </label>
        <label style={{ flex: 1 }}>
          Motif (facultatif) :
          <input type="text" value={nouvelleFermeture.motif}
                 placeholder="Congés, travaux, jour férié..."
                 onChange={(e) => setNouvelleFermeture({...nouvelleFermeture, motif: e.target.value})} />
        </label>
        <button className="btn-primary" onClick={addFermeture}>+ Ajouter</button>
      </div>

      {fermetures.length === 0 ? (
        <p className="muted">Aucune fermeture exceptionnelle prévue.</p>
      ) : (
        <table className="biblio-admin-table">
          <thead>
            <tr><th>Du</th><th>Au</th><th>Motif</th><th></th></tr>
          </thead>
          <tbody>
            {fermetures.map((f) => (
              <tr key={f.id}>
                <td>{f.date_debut}</td>
                <td>{f.date_fin}</td>
                <td>{f.motif || <span className="muted">—</span>}</td>
                <td>
                  <button className="link-btn danger" onClick={() => setConfirmDelFermeture(f)}>
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmDelFermeture && (
        <ConfirmModal
          titre="Supprimer cette fermeture"
          message={`Supprimer la fermeture du ${confirmDelFermeture.date_debut} au ${confirmDelFermeture.date_fin} ?`}
          confirmLabel="Supprimer"
          danger
          onConfirm={removeFermeture}
          onCancel={() => setConfirmDelFermeture(null)}
        />
      )}
    </div>
  );
}
